import React, { useCallback, useEffect, useState } from 'react';
import { JobStage } from '../../entities/JobStage';
import { SelectOption } from '../Select';
import { LabelledSelect } from '../LabelledSelect';
import { usePipelineJobStages } from '../../queries/v1/applicant_tracking/usePipelineJobStages';

type PropTypes = {
  jobId?: number;
  jobStageId?: number;
  disabled?: boolean;
  mandatory?: boolean;
  onChange: (option: SelectOption) => void;
};

const jobToSelectOption = (jobStage: JobStage): SelectOption => {
  return {
    value: jobStage.id.toString(),
    label: jobStage.name,
  };
};

const jobsComparator = (jobStageA: JobStage, jobStageB: JobStage) =>
  (jobStageA?.order ?? 0) - (jobStageB.order ?? 0);

function JobStageSelect({
  jobId,
  jobStageId,
  onChange,
  disabled,
  mandatory = true,
}: PropTypes) {
  const { data: pipelineJobStages, isLoading } = usePipelineJobStages(jobId);

  const jobStages: JobStage[] =
    pipelineJobStages
      ?.map((pipelineJobStage) => pipelineJobStage.jobStage)
      ?.sort(jobsComparator) ?? [];

  const selectOptions: SelectOption[] = jobStages?.map(jobToSelectOption) || [];

  const [selected, setSelected] = useState<SelectOption | undefined>(undefined);

  const handleOnChange = useCallback(
    (option: SelectOption) => {
      setSelected(option);
      onChange(option);
    },
    [onChange],
  );

  useEffect(() => {
    if (jobStages && !selected && !isLoading && jobStages.length > 0) {
      const currentJobStage =
        !!jobStageId && jobStages.find((so) => so.id === jobStageId);

      handleOnChange(jobToSelectOption(currentJobStage || jobStages[0]));
    }

    // the first value got selected by default the fetch took more time and it received the current stage
    // after the selected got filled
    if (
      jobStages &&
      selected &&
      jobStageId &&
      selected.value !== jobStageId.toString()
    ) {
      const currentJobStage = jobStages.find((so) => so.id === jobStageId);

      if (currentJobStage) {
        handleOnChange(jobToSelectOption(currentJobStage));
      }
    }
  }, [jobStages, selected, handleOnChange, isLoading]);

  useEffect(() => {
    setSelected(undefined);
  }, [jobId]);

  return (
    <LabelledSelect
      onChange={handleOnChange}
      label='Job Stage'
      options={selectOptions}
      className='fs-5'
      labelClassName='fw-semibold'
      selected={selected}
      disabled={!jobId || disabled}
      placeholder='Select Job Stage'
      mandatory={mandatory}
      isSearchable={true}
      isClearable={false}
      isLoading={isLoading}
    />
  );
}

export default JobStageSelect;
